import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { object } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import styled, { keyframes, css } from 'styled-components';
import { capitalize } from 'lodash';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from '@ag-grid-community/react';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { dateValueFormatter, dateWithoutTimeComparator, currentLocaleText } from '../../lib/ag-grid';
import PageWrapper from '../PageWrapper';
import TableTabs from '../reusable/TableTabs';
import BulkActionsSidebar from '../BulkActionsSidebar';
import reportsBulkActions, { findReportFormat } from '../../table/bulkActions/reports';
import useBulkSidebarState from '../../hooks/useBulkSidebarState';
import downloadZippedArchiveFromReports from './reports.utils';
import {
  setSelectedReports,
  setActiveReportsTab,
  fetchReports,
  deleteReports,
  markReportsAsViewed,
} from '../../store/reports/actions';
import { selectReportsTabs } from '../../store/reports/selectors';
import * as actions from '../../table/actionsList';
import ConfirmationModal from '../reusable/ConfirmationModal';
import createToastNotification from '../../utils/createToastNotification';
import isChineseEnv from '../../utils/isChineseEnv';
import { ReactComponent as CompletedIcon } from '../../assets/icons/check.svg';
import { ReactComponent as UnreadIcon } from '../../assets/icons/unread.svg';
import { ReactComponent as ProgressIcon } from '../../assets/icons/progress.svg';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning.svg';
import { ReactComponent as ExcelIcon } from '../../assets/icons/report-excel.svg';
import { ReactComponent as ZipIcon } from '../../assets/icons/report-zip.svg';
import { ReactComponent as PdfIcon } from '../../assets/icons/report-pdf.svg';
import { ReactComponent as DocIcon } from '../../assets/icons/report-doc.svg';
import NewModal from '../reusable/NewModal';
import CustomButton from '../reusable/Buttons/Button';
import { regenerateReport } from '../../api';

ModuleRegistry.registerModules([ClientSideRowModelModule, StatusBarModule, SetFilterModule]);
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-068280}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Psytech_International_Ltd}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{GeneSys}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{GeneSys}_need_to_be_licensed___{GeneSys}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{4_October_2025}____[v3]_[01]_MTc1OTUzMjQwMDAwMA==7bc27880cfb7d8f1c736be6bd6fb9894',
);

const Reports = ({ location: { pathname } }) => {
  const reports = useSelector((state) => state.reports.reports);
  const selectedReports = useSelector((state) => state.reports.selectedReports);
  const tabs = useSelector(selectReportsTabs);
  const activeTab = useSelector((state) => state.reports.activeTab);
  const [regenerateReports, setRegenerateReports] = useState([]);
  const [regenerateReportsModalError, setRegenerateReportsModalError] = useState('');
  const gridApiRef = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);

  const [isLoading, setLoadingStatus] = useState(true);

  const fetchData = useCallback(({ tableData, shouldReset, showSpinner, tableCallback }) => {
    return new Promise((res, rej) => {
      if (showSpinner) setLoadingStatus(true);
      dispatch(
        fetchReports(tableData, {
          callback: () => {
            setLoadingStatus(false);
            // eslint-disable-next-line no-unused-expressions
            tableCallback && tableCallback();
            res();
          },
          shouldReset,
        }),
      );
    });
  }, []);

  const [isDeletionModalVisible, setDeletionModalStatus] = useState(false);

  const closeConfirmationModal = () => setDeletionModalStatus(false);
  const openConfirmationModal = () => setDeletionModalStatus(true);

  const onDeleteConfirm = () => {
    const data = selectedReports.map((report) => ({
      reportType: report.reportType,
      notificationID: report.notificationID || null,
      requestID: report.requestID || null,
    }));
    dispatch(
      deleteReports(data, () => {
        closeConfirmationModal();
        fetchData({ shouldReset: true });
      }),
    );
  };

  const onDownloadClick = (reports) => {
    reports.forEach((report) => {
      const link = document.createElement('a');
      const reportDownloadUrl = report.downloadLink;

      const proxyUrl = new URL(`${window._env_.REACT_APP_API}/api/v2/Reports/proxy`);
      proxyUrl.searchParams.set('url', reportDownloadUrl);
      proxyUrl.searchParams.set('forceDirectDownload', 'true');

      link.setAttribute('target', '_blank');
      link.href = proxyUrl.toString();
      const fileName = reportDownloadUrl.substr(report.downloadLink.lastIndexOf('/') + 1);
      link.download = fileName;
      link.click();
    });
    dispatch(markReportsAsViewed(reports.map((item) => item.notificationID)));
  };

  const onOpenClick = (reports) => {
    reports.forEach((report) => {
      const link = document.createElement('a');
      const reportDownloadUrl = report.downloadLink;

      const proxyUrl = new URL(`${window._env_.REACT_APP_API}/api/v2/Reports/proxy`);
      proxyUrl.searchParams.set('url', reportDownloadUrl);
      proxyUrl.searchParams.set('forceDirectDownload', 'false');

      link.setAttribute('target', '_blank');
      link.href = proxyUrl.toString();
      const fileName = reportDownloadUrl.substr(report.downloadLink.lastIndexOf('/') + 1);
      link.download = fileName;
      link.click();
    });
    dispatch(markReportsAsViewed(reports.map((item) => item.notificationID)));
  };

  // Controlling bulk actions sidebar state
  const { isSidebarOpen, closeSidebar } = useBulkSidebarState({ items: selectedReports });

  // BULK ACTIONS
  const bulkActionsHandlers = {
    [actions.OPEN_REPORT]: () => {
      const expiredReports = selectedReports.filter((report) => isReportExpired(report));
      if (expiredReports.length) {
        setRegenerateReports(expiredReports);
        return;
      }
      onOpenClick(selectedReports);
    },
    [actions.DOWNLOAD_REPORT]: () => {
      const expiredReports = selectedReports.filter((report) => isReportExpired(report));
      if (expiredReports.length) {
        setRegenerateReports(expiredReports);
        return;
      }
      if (selectedReports.length > 50) {
        createToastNotification({
          message: I18n.t('downloadLimitToast'),
          type: 'error',
        });
        return;
      }

      if (selectedReports.length > 1)
        downloadZippedArchiveFromReports(
          selectedReports.filter((item) => item.reportType !== 1),
          () => {
            dispatch(markReportsAsViewed(selectedReports.map((item) => item.notificationID)));
          },
        );
      else onDownloadClick(selectedReports);
    },
    [actions.DELETE_REPORT]: openConfirmationModal,
  };

  const bulkActions = useMemo(() => {
    return reportsBulkActions.map((item) => ({
      ...item,
      handler: bulkActionsHandlers[item.name],
      isDisabled: typeof item.isDisabled === 'function' ? item.isDisabled(selectedReports) : item.isDisabled,
    }));
  }, [selectedReports]);

  const onTabClick = (tab) => {
    dispatch(setActiveReportsTab(tab));
  };

  const iconsMap = {
    viewed: CompletedIcon,
    unread: UnreadIcon,
    pending: ProgressIcon,
    error: WarningIcon,
  };

  const styleFormatIcon = (Icon) => styled(Icon)`
    margin-right: 1rem;
  `;

  const formatIconsMap = {
    zip: styleFormatIcon(ZipIcon),
    pdf: styleFormatIcon(PdfIcon),
    xlsx: styleFormatIcon(ExcelIcon),
    doc: styleFormatIcon(DocIcon),
    docx: styleFormatIcon(DocIcon),
  };

  const rotate = keyframes`
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  `;

  const animation = () =>
    css`
      ${rotate} 2s infinite linear;
    `;

  const styleIcon = (Icon, status) => styled(Icon)`
    margin-right: 10px;
    width: 18px;
    height: 18px;
    animation: ${() => (status === 'pending' ? animation : 'none')};

    .a {
      ${() =>
        status === 'error' &&
        css`
          fill: ${(props) => props.theme.colors.red};
          stroke: ${(props) => props.theme.colors.red};
          stroke-width: 0.3px;
        `}
    }
  `;

  function isReportExpired(report) {
    const creation = new Date(report.creationDate);
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

    if (creation < twoYearsAgo) {
      return true;
    }
    return false;
  }

  const agGridDefaultSettings = {
    pagination: true,
    paginationPageSize: 100,
    paginationPageSizeSelector: [20, 50, 100],
    rowHeight: '58',
    headerHeight: '58',
    domLayout: 'autoHeight',
    gridOptions: { popupParent: document.body },
    onSelectionChanged: (event) => {
      const selectedData = [];
      event.api.forEachNodeAfterFilter((node) => {
        if (node.isSelected()) {
          selectedData.push(node.data);
        }
      });
      dispatch(setSelectedReports(selectedData));
    },
  };

  const agGriddefaultColumnDefinition = {
    sortable: true,
  };

  const [columnDefinition] = useState([
    {
      headerName: I18n.t('PersonName'),
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      flex: 2,
      valueGetter: (params) => {
        const fullName = isChineseEnv
          ? `${params.data.familyName || ''}${params.data.firstName}`
          : `${params.data.firstName} ${params.data.familyName}`;
        return fullName;
      },
      cellRenderer: ({ data }) => {
        const mainName = isChineseEnv
          ? `${data.familyName || ''}${data.firstName}`
          : `${data.firstName} ${data.familyName}`;
        const respondentsLength = data.respondentIDs.split(',').length - 1;
        const Icon = formatIconsMap[findReportFormat(data)];
        return (
          <Row>
            {data.downloadLink && Icon && <Icon />}
            <span>{mainName} </span>
            {respondentsLength > 0 && (
              <span style={{ fontStyle: 'italic', fontSize: '8px' }}>and {respondentsLength} more</span>
            )}
          </Row>
        );
      },
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'questionnaire',
      headerName: I18n.t('Report'),
      cellDataType: 'text',
      filter: 'agSetColumnFilter',
      flex: 1,
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'status',
      headerName: I18n.t('Status'),
      cellDataType: 'text',
      filter: 'agSetColumnFilter',
      flex: 1,
      cellRenderer: ({ data }) => {
        const { status, error } = data;
        const finalizedStatus = error ? 'error' : status;
        const Icon = status !== 'deleted' && styleIcon(iconsMap[finalizedStatus], finalizedStatus);
        return (
          <StatusContainer>
            {status !== 'deleted' && <Icon />}
            <StatusLabel error={error || isReportExpired(data)}>
              {finalizedStatus === 'error'
                ? I18n.t('Support ticket raised')
                : isReportExpired(data)
                ? I18n.t('Expired')
                : I18n.t(capitalize(finalizedStatus))}
            </StatusLabel>
          </StatusContainer>
        );
      },
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'creationDate',
      headerName: I18n.t('Creation Date'),
      cellDataType: 'date',
      flex: 1,
      filter: 'agDateColumnFilter',
      valueGetter: (params) => {
        return new Date(params.data.creationDate);
      },
      valueFormatter: dateValueFormatter,
      filterParams: {
        maxNumConditions: 1,
        comparator: dateWithoutTimeComparator,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
  ]);

  const rowSelection = useMemo(() => {
    return {
      mode: 'multiRow',
    };
  }, []);

  const handleFilterChanged = (params) => {
    params.api.deselectAll();
    params.api.refreshHeader();
  };

  function getRowClass(params) {
    if (params.data.reportType === 2) {
      return 'row-unread';
    }
    return null;
  }

  useEffect(() => {
    fetchData({ shouldReset: true, showSpinner: true });
  }, [activeTab]);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    params.api.applyColumnState({
      defaultState: { sort: null },
      state: [{ colId: 'creationDate', sort: 'desc' }],
    });
  }, []);

  const getRowId = useCallback((params) => {
    return params.data.reportRequestID;
  }, []);

  return (
    <PageWrapper title={I18n.t('Reports')} style={{ overflowY: 'auto' }}>
      <BulkActionsSidebar actions={bulkActions} isOpen={isSidebarOpen} onClose={closeSidebar} />
      <TableTabs tabs={tabs} clickHandler={onTabClick} activeTab={activeTab} />
      <div className="ag-theme-quartz ag-grid-custom-styles">
        <AgGridReact
          {...agGridDefaultSettings}
          defaultColDef={agGriddefaultColumnDefinition}
          columnDefs={columnDefinition}
          rowData={isLoading ? null : reports}
          rowSelection={rowSelection}
          onFilterChanged={handleFilterChanged}
          getRowClass={getRowClass}
          localeText={currentLocaleText}
          onGridReady={onGridReady}
          getRowId={getRowId}
        />
      </div>
      <ConfirmationModal
        title={I18n.t('Report deletion')}
        onClose={closeConfirmationModal}
        isVisible={isDeletionModalVisible}
        description={I18n.t('Are you sure you want to delete selected report(s)?')}
        onConfirm={onDeleteConfirm}
        caution
      />

      <StyledModal
        isVisible={!!regenerateReports.length}
        title={I18n.t('Regenerate expired reports')}
        onClose={() => {
          setRegenerateReports([]);
          setRegenerateReportsModalError('');
        }}
      >
        <StyledModalBody>
          <div style={{ marginBottom: '12px', fontSize: '18px', color: '#000', textAlign: 'center' }}>
            {I18n.t('These selected report(s) have expired_ Would you like to regenerate them?')}
          </div>
          {regenerateReports.map((report) => (
            <div key={report.id}>{`${report.firstName} ${report.familyName} ${
              report.questionnaire
            } (${dateValueFormatter({ value: report.creationDate })})`}</div>
          ))}
        </StyledModalBody>
        <ButtonsWrapper>
          <StyledButton
            variant="secondary"
            handler={() => {
              setRegenerateReports([]);
              setRegenerateReportsModalError('');
            }}
          >
            {I18n.t('Cancel')}
          </StyledButton>
          <StyledButton
            variant="primary"
            handler={() => {
              setRegenerateReportsModalError('');
              const regenerateRquests = regenerateReports.map((report) => regenerateReport(report.reportRequestID));
              Promise.allSettled(regenerateRquests).then(async (results) => {
                const [resolved, rejected] = results.reduce(
                  (acc, result, idx) => {
                    if (result.status === 'fulfilled') {
                      acc[0].push([result, regenerateReports[idx]]);
                    } else {
                      acc[1].push([result, regenerateReports[idx]]);
                    }
                    return acc;
                  },
                  [[], []],
                );
                if (rejected.length) {
                  const errorMessage = rejected
                    .map(
                      ([rejection, report]) =>
                        `${rejection.reason || I18n.t('Unknown error')}: ${report.firstName} ${report.familyName} ${
                          report.questionnaire
                        } (${dateValueFormatter({ value: report.creationDate })})`,
                    )
                    .join('\n');
                  setRegenerateReportsModalError(errorMessage);
                  if (resolved.length) {
                    const resolvedReports = resolved.map(([, report]) => report);
                    setRegenerateReports(regenerateReports.filter((report) => !resolvedReports.includes(report)));
                  }
                }

                if (resolved.length) {
                  const resolvedReports = resolved.map(([, report]) => report);
                  const unselectReports = selectedReports.filter((report) => resolvedReports.includes(report));
                  const unselectReportsNodes = [];
                  unselectReports.forEach(({ reportRequestID: id }) => {
                    const node = gridApiRef.current.getRowNode(String(id));
                    if (node) unselectReportsNodes.push(node);
                  });
                  gridApiRef.current.setNodesSelected({
                    nodes: unselectReportsNodes,
                    newValue: false,
                    clearSelection: false,
                    source: 'programmatic',
                  });
                  await fetchData({ shouldReset: true });
                }

                if (rejected.length === 0) {
                  setRegenerateReportsModalError('');
                  setRegenerateReports([]);
                  createToastNotification({
                    message: I18n.t('Reports regenerated successfully'),
                    type: 'success',
                  });
                }
              });
            }}
          >
            {I18n.t('Regenerate')}
          </StyledButton>
        </ButtonsWrapper>
        {regenerateReportsModalError && (
          <div style={{ color: 'red', fontSize: '14px', marginTop: '12px' }}>{regenerateReportsModalError}</div>
        )}
      </StyledModal>
    </PageWrapper>
  );
};

Reports.propTypes = {
  location: object,
};

Reports.defaultProps = {
  location: {},
};

const StyledModal = styled(NewModal)`
  width: 45rem;
  max-height: 63rem;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const StyledModalBody = styled.div`
  overflow: auto;
  max-height: 60rem;
  max-width: 120rem;
`;

const ButtonsWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 10px;
  width: 100%;
  justify-content: end;
`;

const StyledButton = styled(CustomButton)`
  width: 15rem;
  text-transform: uppercase;
`;
const StatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.error ? 'red' : props.theme.colors.primary)};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

export default Reports;
